import { apiClient } from './apiClient';
import { resolveApiErrorMessage } from '../utils/authFormUtils';
import type { IpttqRecord, TtqaPathRun } from '@kelilearning/psycheflow-contracts';
import type { ConvergingTtqRecord } from './cttqService';
import { notifyHomeTtqBurdenStatsInvalidated } from './homeTtqBurdenInvalidation';
import {
  afterTtqSessionWrite,
  invalidateTtqSessionDetail,
  invalidateTtqSessionList,
  peekTtqSessionDetail,
  rememberTtqSessionDetail,
} from './ttqSessionListCache';
import { resolveSubjectCode } from '../utils/subjectConfig';

export type { ConvergingTtqRecord };

export type TtqSubjectCode = 'math' | 'yuwen';

export type TtqPipelineStatus =
  | 'pending'
  | 'pap_running'
  | 'pap_failed'
  | 'analyzing'
  | 'done';

export type TasteVerdict = 'worth_tasting' | 'marginal' | 'not_worth';

export interface TasteAnalysis {
  scores: {
    crossLink: number;
    antiPattern: number;
    ideaExpression: number;
    chainLength: number;
  };
  verdict: TasteVerdict;
  rationale: string;
  analyzedAt: string;
}

export interface FrameworkMatch {
  frameworkNodeId: string;
  reason: string;
  phase?: string;
}

export interface OpeningTaste {
  entrySimplicity: number;
  progressionClarity: number;
  narrative: string;
  suggestedFirstStep: string;
}

export interface BreakthroughTaste {
  breakthroughCount: number;
  items: Array<{
    description: string;
    frameworkNodeId?: string;
    difficultyJump: 'low' | 'medium' | 'high';
  }>;
}

export interface PapFailure {
  message: string;
  occurredAt: string;
}

export interface PrivateActivityPackage {
  version: number;
  subjectValueProfileId: string;
  explicitSpineBinding: Record<string, unknown>;
  examPointFlowProjection: Record<string, unknown>;
  implicitAbilityProjection: Record<string, unknown>;
  ontologyProjection: Record<string, unknown>;
  admission: { status: 'ready' | 'partial'; warnings: string[] };
  extractedAt: string;
}

export interface VariantInterleaveSuggestion {
  specialToGeneral: string;
  formalSimilarity: string;
  knowledgeRelay: string;
  suggestedOrder?: string[];
  narrative: string;
}

export interface TtqSessionParticleEntry {
  id: string;
  sessionId: string;
  particleId: string;
  ordinal: number;
  addedAt: string;
  pipelineStatus: TtqPipelineStatus;
  papFailure?: PapFailure | null;
  pap?: PrivateActivityPackage | null;
  taste?: TasteAnalysis | null;
  frameworkMatches?: FrameworkMatch[] | null;
  openingTaste?: OpeningTaste | null;
  breakthroughTaste?: BreakthroughTaste | null;
  particleTitle?: string | null;
  particleQuestions?: string | null;
  /** 主/子答案；服务端刷新标题用答案而非题干 */
  particleAnswers?: string | null;
}


export interface TtqSession {
  id: string;
  userId: string;
  subjectCode: TtqSubjectCode;
  status: 'draft' | 'active' | 'completed';
  title?: string | null;
  summary?: string | null;
  correlationAnalysis?: Record<string, unknown> | null;
  variantInterleaveSuggestion?: VariantInterleaveSuggestion | null;
  aggregationStale: boolean;
  aggregationAvailable: boolean;
  createdAt?: string;
  updatedAt?: string;
  particles?: TtqSessionParticleEntry[];
  pathRuns?: TtqaPathRun[];
  cttqRecords?: ConvergingTtqRecord[];
  primaryCttqRecordId?: string | null;
  /** 品题浓缩结果 IPTTQ（新建 TTQA 默认至少 1 条草稿） */
  ipttqRecords?: IpttqRecord[];
  /** 列表接口附带：非 archived 的品题浓缩结果条数 */
  ipttqCount?: number;
  /** 列表接口附带：是否已有真正生成的品题浓缩结果 */
  hasCompleteGeneratedIpttq?: boolean;
  /** 列表接口附带：浓缩结果工具栏是否绿灯就绪 */
  ipttqToolbarReady?: boolean;
  /** 列表接口附带：默认/首个 ready 直觉草图预览 */
  iskPreview?: {
    imageUrl?: string | null;
    imageOssId?: string | null;
  } | null;
  /** 列表接口附带：ready 直觉草图预览（最多 3） */
  iskPreviews?: Array<{
    imageUrl?: string | null;
    imageOssId?: string | null;
  }> | null;
  /** 服务端 TTQIB 画布状态（需 migration 088） */
  ttqibState?: Record<string, unknown> | null;
  /** 本品题会话累计 token（ledger 自动累加） */
  tokenUsageSummary?: { totalTokens?: number } | null;
}

export interface TtqGuidanceBlock {
  id: string;
  phase: string;
  role: 'system' | 'user';
  contentMarkdown: string;
  contentStructured?: Record<string, unknown> | null;
  generatedAt: string;
}

export interface TtqGuidanceSession {
  id: string;
  ttqaId: string;
  selectedParticleIds: string[];
  selectionMode: 'single' | 'multiple';
  status: string;
  blocks: TtqGuidanceBlock[];
  chosenAngleId?: string | null;
  chosenQuestionChainVariantId?: string | null;
  createdAt: string;
  updatedAt: string;
  executedAt?: string | null;
}

export type TtqLlmOptions = {
  provider?: string;
  model?: string;
  mode?: 'chat' | 'reasoner';
};

export const ttqService = {
  listSessions(subject: TtqSubjectCode) {
    return apiClient.getEnvelope<TtqSession[]>(`/api/ttq/sessions?subject=${encodeURIComponent(subject)}`);
  },

  /** 查询颗粒是否已加入用户的品题会话（可再次加入，仅作提示） */
  getParticleMemberships(input: {
    particleIds: string[];
    subject?: TtqSubjectCode;
  }) {
    return apiClient.postEnvelope<Array<{
      particleId: string;
      sessionCount: number;
      latestTitle: string | null;
      sessionIds?: string[];
    }>>('/api/ttq/particle-memberships', {
      particleIds: input.particleIds,
      subject: input.subject,
    });
  },

  createSession(subjectCode: TtqSubjectCode, title?: string) {
    return apiClient.postEnvelope<TtqSession>('/api/ttq/sessions', { subjectCode, title }).then((session) => {
      notifyHomeTtqBurdenStatsInvalidated();
      return session;
    });
  },

  createSessionWithParticle(subjectCode: TtqSubjectCode, particleId: string, title?: string) {
    return apiClient.postEnvelope<TtqSession>('/api/ttq/sessions', {
      subjectCode,
      particleId,
      title,
    }).then((session) => {
      notifyHomeTtqBurdenStatsInvalidated();
      return session;
    });
  },

  getSession(sessionId: string, options?: { forceRefresh?: boolean }) {
    const id = String(sessionId || '').trim();
    if (!options?.forceRefresh) {
      const cached = peekTtqSessionDetail(id);
      if (cached) {
        return Promise.resolve(cached);
      }
    }
    return apiClient.getEnvelope<TtqSession>(`/api/ttq/sessions/${encodeURIComponent(id)}`)
      .then((session) => rememberTtqSessionDetail(session));
  },

  /** 持久化 TTQIB 画布状态（含鸟瞰钉住浮窗） */
  patchTtqibState(sessionId: string, ttqibState: Record<string, unknown>) {
    return apiClient.requestJson<{ success?: boolean; data?: TtqSession; error?: string }>(
      `/api/ttq/sessions/${encodeURIComponent(sessionId)}/ttqib-state`,
      {
        method: 'PUT',
        auth: true,
        body: { ttqibState },
      },
    ).then((payload) => {
      if (!payload?.success || !payload.data) {
        throw new Error(resolveApiErrorMessage(payload, '保存画布状态失败'));
      }
      return rememberTtqSessionDetail(payload.data);
    });
  },

  ensureSessionTitle(sessionId: string, options?: { force?: boolean }) {
    const force = Boolean(options?.force);
    return apiClient.requestJson<{
      success?: boolean;
      data?: TtqSession;
      meta?: { changed?: boolean; message?: string };
      error?: string;
    }>(
      `/api/ttq/sessions/${encodeURIComponent(sessionId)}/ensure-title`,
      {
        method: 'POST',
        body: { force },
        auth: true,
        // force：后端 12s LLM + 余量；避免前端先超时
        timeoutMs: force ? 30_000 : undefined,
      },
    ).then((payload) => {
      if (!payload?.success || !payload.data) {
        throw new Error(resolveApiErrorMessage(payload, '刷新标题失败'));
      }
      return {
        session: rememberTtqSessionDetail(payload.data),
        changed: Boolean(payload.meta?.changed),
        message: String(payload.meta?.message || '').trim()
          || (payload.meta?.changed ? '标题已更新' : '标题未变化'),
      };
    });
  },

  deleteSession(sessionId: string) {
    return apiClient.requestJson<{ success?: boolean; data?: { deleted: boolean; mode?: 'hard' | 'soft' }; error?: string }>(
      `/api/ttq/sessions/${encodeURIComponent(sessionId)}`,
      { method: 'DELETE', auth: true },
    ).then((payload) => {
      if (!payload?.success || !payload.data) {
        throw new Error(resolveApiErrorMessage(payload, '删除品题失败'));
      }
      invalidateTtqSessionDetail(sessionId);
      invalidateTtqSessionList();
      notifyHomeTtqBurdenStatsInvalidated();
      return payload.data;
    });
  },

  async addParticles(sessionId: string, particleIds: string[]) {
    const uniqueParticleIds = [...new Set(particleIds)];
    let session = await ttqService.getSession(sessionId);
    const addedIds: string[] = [];
    const skippedExistingIds: string[] = [];
    const failures: Array<{ particleId: string; message: string }> = [];

    for (const particleId of uniqueParticleIds) {
      try {
        session = await apiClient.postEnvelope<TtqSession>(
          `/api/ttq/sessions/${encodeURIComponent(sessionId)}/particles`,
          { particleId },
        );
        addedIds.push(particleId);
      } catch (error) {
        const message = error instanceof Error ? error.message : '添加颗粒失败';
        if (message.includes('已在本会话中')) {
          skippedExistingIds.push(particleId);
          continue;
        }
        failures.push({ particleId, message });
      }
    }

    if (addedIds.length === 0 && skippedExistingIds.length === 0 && failures.length > 0) {
      throw new Error(failures[0].message);
    }

    if (addedIds.length > 0) {
      notifyHomeTtqBurdenStatsInvalidated();
    }

    return { session, addedIds, skippedExistingIds, failures };
  },

  removeParticle(sessionId: string, particleId: string) {
    return apiClient.deleteEnvelope<TtqSession>(
      `/api/ttq/sessions/${encodeURIComponent(sessionId)}/particles/${encodeURIComponent(particleId)}`,
    ).then((session) => {
      notifyHomeTtqBurdenStatsInvalidated();
      return session;
    });
  },

  reextractPap(sessionId: string, entryId: string) {
    return apiClient.postEnvelope<TtqSession>(
      `/api/ttq/sessions/${encodeURIComponent(sessionId)}/particles/${encodeURIComponent(entryId)}/pap/reextract`,
      {},
    );
  },

  runSessionA(sessionId: string, options?: TtqLlmOptions) {
    return apiClient.postEnvelope<TtqSession>(
      `/api/ttq/sessions/${encodeURIComponent(sessionId)}/ttqSessionA`,
      options ?? {},
    );
  },

  runSessionB(sessionId: string, options?: TtqLlmOptions) {
    return apiClient.postEnvelope<TtqSession>(
      `/api/ttq/sessions/${encodeURIComponent(sessionId)}/ttqSessionB`,
      options ?? {},
    );
  },

  runAggregation(sessionId: string, options?: TtqLlmOptions) {
    return apiClient.postEnvelope<TtqSession>(
      `/api/ttq/sessions/${encodeURIComponent(sessionId)}/aggregate`,
      options ?? {},
    );
  },

  completeSession(sessionId: string, createAnchorIdentity = false) {
    return apiClient.postEnvelope<TtqSession>(
      `/api/ttq/sessions/${encodeURIComponent(sessionId)}/complete`,
      { createAnchorIdentity },
    ).then((session) => {
      notifyHomeTtqBurdenStatsInvalidated();
      return session;
    });
  },

  createGuidanceSession(sessionId: string, selectedParticleIds: string[]) {
    return apiClient.postEnvelope<{ session: TtqGuidanceSession; blocks: TtqGuidanceBlock[] }>(
      `/api/ttq/sessions/${encodeURIComponent(sessionId)}/guidance/sessions`,
      { selectedParticleIds },
    );
  },

  previewGuidanceAngle(
    sessionId: string,
    guidanceId: string,
    angleId: string,
    questionChainVariantId: string | null,
  ) {
    return apiClient.postEnvelope<{ session: TtqGuidanceSession; block: TtqGuidanceBlock }>(
      `/api/ttq/sessions/${encodeURIComponent(sessionId)}/guidance/sessions/${encodeURIComponent(guidanceId)}/angles/${encodeURIComponent(angleId)}/preview`,
      { questionChainVariantId },
    );
  },

  confirmGuidanceSession(
    sessionId: string,
    guidanceId: string,
    angleId: string,
    questionChainVariantId: string | null,
  ) {
    return apiClient.postEnvelope<{ session: TtqGuidanceSession; confirmBlock: TtqGuidanceBlock }>(
      `/api/ttq/sessions/${encodeURIComponent(sessionId)}/guidance/sessions/${encodeURIComponent(guidanceId)}/confirm`,
      { angleId, questionChainVariantId },
    );
  },

  runGuidanceSession(
    sessionId: string,
    guidanceId: string,
    body?: { questionChainVariantId?: string; batchSize?: number },
  ) {
    return apiClient.postEnvelope<{ session: TtqGuidanceSession; ttqa: TtqSession }>(
      `/api/ttq/sessions/${encodeURIComponent(sessionId)}/guidance/sessions/${encodeURIComponent(guidanceId)}/run`,
      body ?? {},
    );
  },

  assessSessionMerge(input: {
    primarySessionId: string;
    secondarySessionId: string;
    reason: string;
  }) {
    return apiClient.postEnvelope<TtqSessionMergeAssessment>(
      '/api/ttq/sessions/merge/assess',
      input,
    );
  },

  mergeSessions(input: {
    primarySessionId: string;
    secondarySessionId: string;
    reason: string;
    mode: 'force' | 'accept_ai';
    assessmentId?: string;
    assessment?: TtqSessionMergeAssessment;
  }) {
    return apiClient.postEnvelope<TtqSession>(
      '/api/ttq/sessions/merge',
      input,
    ).then((session) => {
      notifyHomeTtqBurdenStatsInvalidated();
      return session;
    });
  },
};

export type TtqSessionMergeAssessment = {
  assessmentId: string;
  primarySessionId: string;
  secondarySessionId: string;
  reason: string;
  hasMergeValue: boolean;
  score: number;
  rationale: string;
  risks: string[];
  suggestedAction: 'merge' | 'keep_separate';
  createdAt: string;
  expiresAt: string;
};

/** WebApp2 品题入口：开发阶段开启；与 Mobile2 对齐时可改为 false */
export const TTQ_USER_ENTRY_ENABLED = true;

/** 将「数学」/ math 等规范化为品题学科码；非 math/yuwen 返回 null。 */
export function resolveTtqSubjectCode(subject: string): TtqSubjectCode | null {
  const code = resolveSubjectCode(subject);
  return code === 'math' || code === 'yuwen' ? code : null;
}

export function isTtqSupportedSubject(subject: string): boolean {
  return resolveTtqSubjectCode(subject) != null;
}

export function resolveTtqDockSubject(subject: string): TtqSubjectCode {
  return resolveTtqSubjectCode(subject) ?? 'math';
}

/** 颗粒详情等「品题」菜单：目前仅数学；兼容「数学」与 math。 */
export function isTtqSubjectEnabled(subject: string): boolean {
  return TTQ_USER_ENTRY_ENABLED && resolveTtqSubjectCode(subject) === 'math';
}

export function canHardDeleteTtqSession(session: TtqSession): boolean {
  const hasParticles = (session.particles?.length ?? 0) > 0;
  const hasCorrelation = Boolean(
    session.correlationAnalysis
    && typeof session.correlationAnalysis === 'object'
    && Object.keys(session.correlationAnalysis).length > 0,
  );
  const hasVariant = Boolean(session.variantInterleaveSuggestion);
  return !hasParticles && !hasCorrelation && !hasVariant;
}
